import { roundDecimal } from "components/helper/numberHelper";
import { useEffect, useState } from "react";
import styled from "styled-components";

type Props = {
    text: string;
    isActive: boolean;
    isDisabled?: boolean;
    color?: string;
    onBlur: (arg0: number) => void;
    remainingWeight: number;
    value?: number;
    onKeyUp: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    currentRef: (el: HTMLInputElement) => void;
    image?: string;
    description?: string;
    nbDecimal: number;
};

interface StyledButtonProps {
    $isActive: boolean;
    $color?: string;
    disabled?: boolean;
}

const DistributionCaseProposal = ({
    text,
    isActive,
    isDisabled,
    color,
    onBlur,
    remainingWeight,
    value,
    onKeyUp,
    currentRef,
    image,
    description,
    nbDecimal,
}: Props) => {
    const [localValue, setLocalValue] = useState<
        number | string | undefined | ""
    >(value);

    const hasImage = !!image;
    const imgSrc = hasImage ? `${image}` : undefined;

    //After reset the vote, need this ⬇️, for reset each input
    useEffect(() => {
        setLocalValue(value !== undefined ? value.toString() : "");
    }, [value]);

    const handleChange = (e: { target: HTMLElement & { value: string } }) => {
        const input = e.target;

        // On a mobile with default arrow keys, the focus is not always forced
        // This forces the focus as it is needed to get the onBlur function called
        if (document.activeElement !== input) {
            input.focus();
        }

        const rawValue = input.value;

        if (!/^[0-9]*[.,]?[0-9]*$/.test(rawValue) && rawValue !== "") {
            return;
        }

        if (rawValue === "") {
            setLocalValue("");
            return;
        }

        const normalized: string = rawValue.replace(",", ".");
        const hasDot: boolean = normalized.includes(".");
        const [intPart, decPart = ""] = normalized.split(".");

        const cleanedIntPart = String(parseInt(intPart, 10));

        const cleanedValue: string = decPart
            ? `${cleanedIntPart}.${decPart.slice(0, nbDecimal)}`
            : cleanedIntPart + (hasDot && nbDecimal > 0 ? "." : "");

        const numericValue: number = parseFloat(cleanedValue);
        if (isNaN(numericValue) || numericValue < 0) return;

        const maxValue: number = roundDecimal(
            remainingWeight + Number(value),
            nbDecimal,
        );
        setLocalValue(numericValue >= maxValue ? maxValue : cleanedValue);
    };

    return (
        <StyledWrapper
            $isActive={isActive}
            $color={color}
            disabled={isDisabled}
        >
            <ContentWrapper>
                {hasImage && (
                    <ProposalImage src={imgSrc} alt={`Image for ${text}`} />
                )}
                <AsideWrapper>
                    <TextWrapper>
                        <StyledPropositionsText>{text}</StyledPropositionsText>
                        {description && (
                            <DescriptionText $isActive={isActive}>
                                {description}
                            </DescriptionText>
                        )}
                    </TextWrapper>
                    <Input
                        type="text"
                        inputMode="decimal"
                        value={localValue || ""}
                        onBlur={() => {
                            onBlur(Number(localValue));
                        }}
                        onChange={handleChange}
                        disabled={isDisabled}
                        $isActive={isActive}
                        data-testactive={isActive}
                        aria-label={`${text}-vote-distribution`}
                        $color={color}
                        onKeyUp={onKeyUp}
                        ref={currentRef}
                    />
                </AsideWrapper>
            </ContentWrapper>
        </StyledWrapper>
    );
};

const StyledWrapper = styled.div<StyledButtonProps>`
    background: ${(props) =>
        props.$isActive ? (props.$color ? props.$color : "#60b860") : null};
    color: ${(props) =>
        props.$isActive ? "#ffffff" : `${props.$color || "black"}`};
    border: ${(props) =>
        props.$color
            ? `1px solid ${props.$color}`
            : props.$isActive
              ? "1px solid #33a835"
              : "1px solid grey"};
    margin-bottom: 0.5rem;
    min-height: 2.75rem;
    text-align: center;
    width: 100%;
    border-radius: 4px;

    &[disabled] {
        background: ${(props) =>
            props.$isActive
                ? props.$color
                    ? props.$color
                    : "#60b860"
                : "#e8e8e8"};
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const Input = styled.input<StyledButtonProps>`
    width: 90%;
    margin: 0.5rem 0;
    padding: 3px;
    background: ${(props) =>
        props.$color ? null : props.$isActive ? "#60b860" : "#ffffff"};
    color: ${(props) =>
        props.$isActive ? (props.$color ? props.$color : "#ffffff") : null};
    border: 1px solid black;
    border-radius: 4px;
    text-align: center;
    font-size: 15px;

    &[disabled] {
        color: ${(props) => (props.$color ? "#FFFFFF" : "#000000")};
        background: ${(props) =>
            props.$color ? "#1c1c1c" : props.$isActive ? "#60b860" : "#e8e8e8"};
        opacity: 0.5;
        cursor: not-allowed;
    }

    /* Remove arrow */
    ::-webkit-outer-spin-button,
    ::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
`;

const StyledPropositionsText = styled.div`
    font-size: 14px;
    padding-top: 0.5rem;
`;

const ContentWrapper = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
`;

const ProposalImage = styled.img`
    width: 60px;
    height: 60px;
    object-fit: cover;
    margin-left: 10px;
    border-radius: 4px;
`;

const TextWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 90%;
    text-align: center;
    word-break: break-word;
    white-space: normal;
    max-width: 100%;
`;

const AsideWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    flex: 1;
`;
const DescriptionText = styled.p<{ $isActive?: boolean }>`
    margin: 0;
    font-size: 0.9em;
    color: ${(props) => (props.$isActive ? "#ffffff" : "#555555")};
`;

export default DistributionCaseProposal;
